import React, { useState } from "react";
import { processService } from "../../services/process.service";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Plus, Trash, Edit, FileText } from "lucide-react";

const { addProcessoDocumento, updateProcessoDocumento, removeProcessoDocumento } = processService;

export function DocumentsTab({ processo, onRefresh }) {
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [newDocData, setNewDocData] = useState({
    tipo: "", template_token: "", parent_id: null, rodada: 1,
    send_automatic_email: true, send_automatic_whatsapp: false
  });

  const handleSaveDocument = async () => {
    if (!processo) return;
    try {
      if (editingDocId) {
        await updateProcessoDocumento(processo.id, editingDocId, newDocData);
        toast.success("Documento atualizado");
      } else {
        await addProcessoDocumento(processo.id, newDocData);
        toast.success("Documento adicionado");
      }
      setIsDocDialogOpen(false);
      setEditingDocId(null);
      setNewDocData({
        tipo: "", template_token: "", parent_id: null, rodada: 1,
        send_automatic_email: true, send_automatic_whatsapp: false
      });
      onRefresh();
    } catch (e) {
      toast.error("Erro ao salvar documento: " + e.message);
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocId(doc.id);
    setNewDocData({
      tipo: doc.tipo,
      template_token: doc.template_token,
      parent_id: doc.parent_id,
      rodada: doc.rodada ?? 1,
      send_automatic_email: doc.send_automatic_email ?? true,
      send_automatic_whatsapp: doc.send_automatic_whatsapp ?? false
    });
    setIsDocDialogOpen(true);
  };

  const handleRemoveDocument = async (docId) => {
    if (!processo) return;
    try {
      await removeProcessoDocumento(processo.id, docId);
      toast.success("Documento removido");
      onRefresh();
    } catch (e) {
      toast.error("Erro ao remover documento");
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5" /> Documentos e Tokens
        </h3>
        <Dialog open={isDocDialogOpen} onOpenChange={(open) => {
          setIsDocDialogOpen(open);
          if (!open) {
            setEditingDocId(null);
            setNewDocData({
              tipo: "", template_token: "", parent_id: null, rodada: 1,
              send_automatic_email: true, send_automatic_whatsapp: false
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="mr-2 h-4 w-4" /> Adicionar Documento</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDocId ? "Editar Documento" : "Adicionar Documento"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={newDocData.tipo}
                  onValueChange={(val) => setNewDocData({ ...newDocData, tipo: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo do documento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="termo">Termo</SelectItem>
                    <SelectItem value="cessao">Cessão</SelectItem>
                    <SelectItem value="honorarios">Honorários</SelectItem>
                    <SelectItem value="anuencia">Anuência (2ª Rodada)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Token do Modelo (ZapSign)</Label>
                <Input
                  value={newDocData.template_token}
                  onChange={e => setNewDocData({ ...newDocData, template_token: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Rodada</Label>
                <Select
                  value={String(newDocData.rodada ?? 1)}
                  onValueChange={(val) => setNewDocData({ ...newDocData, rodada: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a rodada" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1ª Rodada — Após aceite do processo</SelectItem>
                    <SelectItem value="2">2ª Rodada — Após aceite da cessão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Documento Principal (Opcional)</Label>
                <Select
                  value={newDocData.parent_id ? String(newDocData.parent_id) : "none"}
                  onValueChange={(val) => setNewDocData({ ...newDocData, parent_id: val === "none" ? null : parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (Principal)</SelectItem>
                    {processo.documentos?.filter(d => !d.parent_id && d.id !== editingDocId).map(doc => (
                      <SelectItem key={doc.id} value={String(doc.id)}>{doc.tipo} ({doc.template_token})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="doc_send_email"
                    checked={newDocData.send_automatic_email}
                    onCheckedChange={(checked) => setNewDocData({ ...newDocData, send_automatic_email: checked })}
                  />
                  <Label htmlFor="doc_send_email">Enviar Email Automático</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="doc_send_whatsapp"
                    checked={newDocData.send_automatic_whatsapp}
                    onCheckedChange={(checked) => setNewDocData({ ...newDocData, send_automatic_whatsapp: checked })}
                  />
                  <Label htmlFor="doc_send_whatsapp">Enviar WhatsApp Automático</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSaveDocument}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tipo</TableHead>
            <TableHead>Token</TableHead>
            <TableHead>Rodada</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {processo.documentos?.filter(d => !d.parent_id).map(doc => (
            <React.Fragment key={doc.id}>
              <TableRow className="bg-slate-50/50">
                <TableCell className="font-medium">{doc.tipo} <span className="text-xs text-slate-500 ml-2">(Principal)</span></TableCell>
                <TableCell className="font-mono text-sm">{doc.template_token}</TableCell>
                <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${doc.rodada === 2 ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{doc.rodada === 2 ? "2ª Rodada" : "1ª Rodada"}</span></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleEditDocument(doc)}>
                    <Edit className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveDocument(doc.id)}>
                    <Trash className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
              {processo.documentos?.filter(child => child.parent_id === doc.id).map(child => (
                <TableRow key={child.id}>
                  <TableCell className="pl-8 flex items-center gap-2">
                    <div className="w-4 h-px bg-slate-300"></div>
                    {child.tipo}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{child.template_token}</TableCell>
                  <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${child.rodada === 2 ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{child.rodada === 2 ? "2ª Rodada" : "1ª Rodada"}</span></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEditDocument(child)}>
                      <Edit className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveDocument(child.id)}>
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
          {processo.documentos?.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-gray-500">Nenhum documento cadastrado</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
